import React, { useEffect, useState } from "react";
import _ from "lodash";
import Grid from "@mui/material/Grid";
import ValidatorSelect from "../Validator/ValidatorSelect";
import * as divorceStatus from "../../services/LeadStatus/StatusDivorce";
import * as settlementStatus from "../../services/LeadStatus/StatusSettlement";
import * as trafficStatus from "../../services/LeadStatus/StatusTraffic";
import * as caseStatus from "../../services/LeadStatus/StatusCase";
import ButtonLoading from "../Button/ButtonLoading";
import MenuItem from "@mui/material/MenuItem";
import ApiClient from "../../services/ApiClient";

import { useSnackbar } from "notistack";
import ValidatorTextField from "../Validator/ValidatorTextField";
import useForm from "../../hooks/useForm";
import { convertToFloat, formValue } from "../../services/formServiceFunctions";
import ValidatorForm from "../Validator/ValidatorForm";
import ContentBox from "../ContentBox/ContentBox";
import { Typography } from "@mui/material";
import { queryKeys } from "../../services/ReactQuery/reactQueryService";
import { useQueryClient } from "@tanstack/react-query";
import { useCurrentUser } from "../../provider/CurrentUserProvider";
import LegalbirdIoConfirm from "../Modal/LegalbirdIoConfirm";
import { invoiceConfirmationConfig } from "../../services/invoiceService";
import ValidatorNumberField from "../Validator/ValidatorNumberField";
import { Roles } from "../../types/BackofficeUser";
import GeneratableFile from "../../types/GeneratableFile";
import { BookingEntry } from "../../types/BookingEntry";
import RelatedInvoiceNumberSelect from "./RelatedInvoiceNumberSelect";
import { userHasOneOfTheseRoles } from "../../services/backofficeUserService";
import { ProductUnion } from "../../types/ProductUnion";

type CaseInvoiceGenerateFormProps = {
  product: ProductUnion;
  bookingEntries: Array<BookingEntry>;
};

const CaseInvoiceGenerateForm = ({ product, bookingEntries }: CaseInvoiceGenerateFormProps) => {
  const currentUser = useCurrentUser();
  const hasAccountingRights = _.intersection(currentUser.roles, [Roles.accounting, Roles.admin]).length > 0;
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [confirmCreateInvoiceOpen, setConfirmCreateInvoiceOpen] = useState(false);

  const generatableInvoicesFamilyLaw: GeneratableFile[] = [
    {
      value: "additionalActionsInvoice",
      label: "Außergerichtliche Folgetätigkeit",
      isDisabled: (product) => {
        if (product.caseInsurance && product.caseInsurance.insurer) {
          return !_.includes([17, 2], product.caseInsurance.insurer.id);
        }
        return true;
      },
      isHidden: () => !hasAccountingRights,
    },
    {
      value: "genericInvoiceCreditNote",
      label: "Allgemeine Rechnung/Gutschrift",
      isDisabled: () => {
        return false;
      },
      isHidden: () => false,
    },
    {
      value: "advisoryFlatFee",
      label: "Erstberatung Partnerversicherung",
      isDisabled: (product) => {
        return !product.caseInsurance.iIWPI;
      },
      isHidden: () => {
        return !hasAccountingRights;
      },
    },
  ];

  const generatableInvoicesLitigationCase: GeneratableFile[] = [
    {
      value: "genericInvoiceCreditNote",
      label: "Allgemeine Rechnung/Gutschrift",
      isDisabled: () => false,
      isHidden: () => false,
    },
    {
      value: "extrajudicialCoverageInvoice",
      label: "Außergerichtliche Rechnung für Nicht-Partnerversicherung",
      isDisabled: (product) => {
        return (
          !product.caseInsurance.extrajudicialCoverageRequestAnsweredDate ||
          !product.caseInsurance.extrajudicialCoverageGranted
        );
      },
      isHidden: () => false,
    },
    {
      value: "extrajudicialFlatFee",
      label: "Außergerichtliche Tätigkeit Partnerversicherung",
      isDisabled: (product) => {
        return product.caseInsurance.iIWPI === false || product.leadStatus < caseStatus.STATUS_DATA_COMPLETED;
      },
      isHidden: () => false,
    },
    {
      value: "advisorySubtractedExtrajudicialFlatFee",
      label: "Außergerichtliche Tätigkeit Partnerversicherung mit Anrechnung",
      isDisabled: (product) => {
        return product.caseInsurance.iIWPI === false || product.leadStatus < caseStatus.STATUS_DATA_COMPLETED;
      },
      isHidden: () => false,
    },
    {
      value: "advisoryFlatFee",
      label: "Erstberatung Partnerversicherung",
      isDisabled: (product) => {
        return !product.caseInsurance.iIWPI;
      },
      isHidden: () => {
        return !hasAccountingRights;
      },
    },
    {
      value: "invoiceExtrajudicialRvg",
      label: "Rechnung außergerichtliche Tätigkeit",
      isDisabled: (product) => {
        return product.leadStatus < caseStatus.STATUS_LAWSUIT_COMPLETED;
      },
      isHidden: () => false,
    },
    {
      value: "caseMonitoring",
      label: "Rechnung Monitoringgebühr",
      isDisabled: (product) => {
        return (
          !(
            product.leadStatus === caseStatus.STATUS_EXTRAJUDICIAL_REACTION_RECEIVED ||
            product.leadStatus === caseStatus.STATUS_ACTION_REQUIRED ||
            product.leadStatus === caseStatus.STATUS_COMPLETED
          ) || product.hasMonitoringBilling !== true
        );
      },
      isHidden: () => product.paymentType !== "insurance" || ![2].includes(product.caseInsurance.insurer?.id ?? -1),
    },
    {
      value: "evaluateJudgementRvg",
      label: "Rechnung Prüfungsgebühr",
      isDisabled: (product) => product.leadStatus < caseStatus.STATUS_EVALUATE_JUDGEMENT,
      isHidden: () => false,
    },
    {
      value: "creditNoteDeductibleLawfirm",
      label: "Gutschrift Selbstbehalt (LBRA)",
      isDisabled: (product) => product.leadStatus < caseStatus.STATUS_CHECKED,
      isHidden: () =>
        !hasAccountingRights && !userHasOneOfTheseRoles(currentUser, [Roles.leadLawyer, Roles.basicAccounting]),
    },
    {
      value: "finalManagementFeeStandardLitigationInvoice",
      label: "Rechnung Managementgebühr RVG Versicherung",
      isDisabled: () => false,
      isHidden: () => !hasAccountingRights,
    },
    {
      value: "creditNoteDeductibleLegalbird",
      label: "Gutschrift Selbstbehalt (R&S)",
      isDisabled: (product) => {
        return (
          product.paymentType === "private" ||
          product.leadStatus < caseStatus.STATUS_PREPARE_LAWSUIT ||
          !bookingEntries.find(
            (bookingEntry) => bookingEntry.invoiceTypeIdentifier === "finalManagementFeeStandardLitigationInvoice"
          )
        );
      },
      isHidden: () => {
        return !hasAccountingRights;
      },
    },
    {
      value: "finalLitigationRvgWithDownPayment",
      label: "Gerichtliche Abrechnung RVG mit Anrechnung Vorschuss",
      isDisabled: (product) =>
        !userHasOneOfTheseRoles(currentUser, [Roles.accounting, Roles.leadLawyer, Roles.admin]) ||
        product.leadStatus < caseStatus.STATUS_LAWSUIT_COMPLETED,
      isHidden: () => false,
    },
  ];

  const generatableInvoices: Record<string, GeneratableFile[]> = {
    divorce: [
      {
        value: "invoiceProcess",
        label: "Scheidung Rechnung Verfahrensgebühr",
        isDisabled: (divorce: ProductUnion) => {
          return divorce.leadStatus < divorceStatus.STATUS_CHECKED;
        },
        isHidden: () => {
          return !hasAccountingRights;
        },
      },
      {
        value: "invoiceHearing",
        label: "Scheidung Rechnung Terminsgebühr",
        isDisabled: (divorce: ProductUnion) => {
          return divorce.leadStatus < divorceStatus.STATUS_CHECKED;
        },
        isHidden: () => {
          return !hasAccountingRights;
        },
      },
      {
        value: "invoiceInstallment",
        label: "Rechnung Ratenzahlung",
        isDisabled: (divorce: ProductUnion) => {
          return divorce.leadStatus < divorceStatus.STATUS_CHECKED;
        },
        isHidden: () => {
          return !hasAccountingRights;
        },
      },
      {
        value: "cancellationFee",
        label: "Scheidung Rechnung 0,8",
        isDisabled: (divorce) => {
          return divorce.leadStatus < divorceStatus.STATUS_POWER_OF_ATTORNEY_GRANTED;
        },
        isHidden: () => false,
      },
      {
        value: "differentialCostsApplication",
        label: "Scheidung Differenzkostenantrag",
        isDisabled: (divorce) => {
          return divorce.leadStatus < divorceStatus.STATUS_COMPLETED;
        },
        isHidden: () => false,
      },
      {
        value: "invoiceFinal",
        label: "Abschlussrechnung / Abschlussgutschrift",
        isDisabled: (divorce) => {
          return divorce.leadStatus < divorceStatus.STATUS_DIVORCE_DONE;
        },
        isHidden: () => {
          return !hasAccountingRights;
        },
      },
      {
        value: "invoiceProcessCostSupportPre",
        label: "Scheidung VKH Vorschusskostenrechnung",
        isDisabled: (divorce) => {
          return divorce.hasProcessCostSupport === false || divorce.leadStatus < divorceStatus.STATUS_CHECKED;
        },
        isHidden: () => false,
      },
      {
        value: "invoiceProcessCostSupportFinal",
        label: "Scheidung VKH Abschlussrechnung",
        isDisabled: (divorce) => {
          return divorce.hasProcessCostSupport === false || divorce.leadStatus < divorceStatus.STATUS_DIVORCE_DONE;
        },
        isHidden: () => {
          return !hasAccountingRights;
        },
      },
      {
        value: "genericInvoiceCreditNote",
        label: "Allgemeine Rechnung/Gutschrift",
        isDisabled: () => {
          return false;
        },
        isHidden: () => false,
      },
      {
        value: "additionalActionsInvoice",
        label: "Außergerichtliche Folgetätigkeit",
        isDisabled: (product) => {
          if (product.caseInsurance && product.caseInsurance.insurer) {
            return !_.includes([17, 2], product.caseInsurance.insurer.id);
          }
          return true;
        },
        isHidden: () => {
          return !hasAccountingRights;
        },
      },
      {
        value: "advisoryFlatFee",
        label: "Erstberatung Partnerversicherung",
        isDisabled: (product) => {
          return !product.caseInsurance.iIWPI;
        },
        isHidden: () => {
          return !hasAccountingRights;
        },
      },
    ],
    settlement: [
      {
        value: "advisoryFlatFee",
        label: "Erstberatung Partnerversicherung",
        isDisabled: (product) => {
          return !product.caseInsurance.iIWPI;
        },
        isHidden: () => {
          return !hasAccountingRights;
        },
      },
      {
        value: "invoiceProcessHearing",
        label: "Rechnung Verfahrens- und Terminsgebühr",
        isDisabled: (settlement) => {
          return settlement.leadStatus < settlementStatus.STATUS_CHECKED;
        },
        isHidden: () => {
          return !hasAccountingRights;
        },
      },
      {
        value: "invoiceSettlement",
        label: "Rechnung Einigungsgebühr",
        isDisabled: (settlement) => {
          return settlement.noWinNoFee === true || settlement.leadStatus < settlementStatus.STATUS_DECISION_MADE;
        },
        isHidden: () => {
          return !hasAccountingRights;
        },
      },
      {
        value: "creditNoteDeductibleLawfirm",
        label: "Gutschrift Selbstbehalt (LBRA)",
        isDisabled: (settlement) => {
          return settlement.noWinNoFee === true || settlement.leadStatus < settlementStatus.STATUS_DECISION_MADE;
        },
        isHidden: () => {
          return !hasAccountingRights;
        },
      },
      {
        value: "creditNoteDeductibleLegalbird",
        label: "Gutschrift Selbstbehalt (R&S)",
        isDisabled: (traffic) => {
          return (
            traffic.paymentType === "private" || traffic.leadStatus < trafficStatus.STATUS_INSURANCE_COVERAGE_REQUEST
          );
        },
        isHidden: () => {
          return !hasAccountingRights;
        },
      },
      {
        value: "genericInvoiceCreditNote",
        label: "Allgemeine Rechnung/Gutschrift",
        isDisabled: () => {
          return false;
        },
        isHidden: () => false,
      },
      {
        value: "extrajudicialFlatFee",
        label: "Außergerichtliche Tätigkeit Partnerversicherung",
        isDisabled: (product) => {
          return product.caseInsurance.iIWPI === false || product.leadStatus < settlementStatus.STATUS_DATA_COMPLETED;
        },
        isHidden: () => false,
      },
    ],
    traffic: [
      {
        value: "invoiceCoverageAndInvoice",
        label: "Deckungsanfrage inkl Anlagen",
        isDisabled: (traffic) => {
          return traffic.legalExpenseInsurance !== true || traffic.leadStatus < trafficStatus.STATUS_CHECKED;
        },
        isHidden: () => {
          return !hasAccountingRights;
        },
      },
      {
        value: "invoiceHearing",
        label: "Rechnung Anwaltsgebühren",
        isDisabled: (traffic) => {
          return traffic.leadStatus < trafficStatus.STATUS_CHECKED;
        },
        isHidden: () => {
          return !hasAccountingRights;
        },
      },
      {
        value: "invoiceFinalPrivatePayer",
        label: "Abschlussrechnung Privatzahler",
        isDisabled: (traffic) => {
          return traffic.paymentType !== "private" || traffic.leadStatus < trafficStatus.STATUS_TRIAL;
        },
        isHidden: () => {
          return !hasAccountingRights;
        },
      },
      {
        value: "creditNoteFinalPrivatePayer",
        label: "Gutschrift Privatzahler",
        isDisabled: (traffic) => {
          return traffic.paymentType !== "private" || traffic.leadStatus < trafficStatus.STATUS_TRIAL;
        },
        isHidden: () => {
          return !hasAccountingRights;
        },
      },
      {
        value: "invoiceFinalInsurer",
        label: "Abschlussrechnung RSV",
        isDisabled: (traffic) => {
          return traffic.paymentType === "private" || traffic.leadStatus < trafficStatus.STATUS_TRIAL;
        },
        isHidden: () => {
          return !hasAccountingRights;
        },
      },
      {
        value: "creditNoteDeductibleLawfirm",
        label: "Gutschrift Selbstbehalt (LBRA)",
        isDisabled: (traffic) => {
          return (
            traffic.paymentType === "private" || traffic.leadStatus < trafficStatus.STATUS_INSURANCE_COVERAGE_REQUEST
          );
        },
        isHidden: () => {
          return !hasAccountingRights;
        },
      },
      {
        value: "creditNoteDeductibleLegalbird",
        label: "Gutschrift Selbstbehalt (R&S)",
        isDisabled: (traffic) => {
          return (
            traffic.paymentType === "private" || traffic.leadStatus < trafficStatus.STATUS_INSURANCE_COVERAGE_REQUEST
          );
        },
        isHidden: () => {
          return !hasAccountingRights;
        },
      },
      {
        value: "genericInvoiceCreditNote",
        label: "Allgemeine Rechnung/Gutschrift",
        isDisabled: () => {
          return false;
        },
        isHidden: () => false,
      },
    ],
    alimony: generatableInvoicesFamilyLaw,
    civilServiceLaw: generatableInvoicesLitigationCase,
    death: generatableInvoicesFamilyLaw,
    familyLawCase: generatableInvoicesFamilyLaw,
    eventContract: generatableInvoicesLitigationCase,
    generalCivilLaw: generatableInvoicesLitigationCase,
    membershipContract: generatableInvoicesLitigationCase,
    otherContract: generatableInvoicesLitigationCase,
    providerContract: generatableInvoicesLitigationCase,
    dataLeakContract: [
      ...generatableInvoicesLitigationCase,
      {
        value: "standardLitigationRvgAdvance",
        label: "Vorschussrechnung RVG",
        isDisabled: (product) =>
          !userHasOneOfTheseRoles(currentUser, [Roles.accounting, Roles.leadLawyer, Roles.admin]) ||
          product.leadStatus < caseStatus.STATUS_PREPARE_LAWSUIT,
        isHidden: () => false,
      },
    ],
    purchaseContract: generatableInvoicesLitigationCase,
    pendingCase: [
      {
        value: "extrajudicialFlatFee",
        label: "Außergerichtliche Tätigkeit Partnerversicherung",
        isDisabled: () => false,
        isHidden: () => false,
      },
    ],
    serviceContract: generatableInvoicesLitigationCase,
    subscriptionContract: generatableInvoicesLitigationCase,
    travelContract: generatableInvoicesLitigationCase,
    trafficAccident: generatableInvoicesLitigationCase,
    vehicleContract: generatableInvoicesLitigationCase,
    rentalContract: generatableInvoicesLitigationCase,
    shortTimeWork: [
      {
        value: "genericInvoiceCreditNote",
        label: "Allgemeine Rechnung/Gutschrift",
        isDisabled: () => false,
        isHidden: () => false,
      },
    ],
    employmentContract: generatableInvoicesLitigationCase,
  };

  const generateInvoice = async () => {
    let paramValues = _.merge({}, values);

    if (paramValues.amount) {
      if (paramValues.amountIsNetOrGrossAmount !== "gross") {
        paramValues.netAmount = convertToFloat(paramValues.amount);
      } else {
        paramValues.netAmount = phpRound(convertToFloat(paramValues.amount) / parseFloat(paramValues.vatOverwrite), 3);
      }
    }

    const response = await ApiClient.post(
      "invoice/generate/" + product.productClassName + "/" + product.id + "/" + values.fileType,
      {
        body: JSON.stringify(paramValues),
      }
    );
    const messageText =
      response?.status === "ok" ? "Rechnung wurde generiert" : "Rechnung konnte nicht generiert werden";

    enqueueSnackbar(messageText, {
      variant: "custom",
      isNonInteractive: true,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center",
      },
      ...(response?.status === "ok" ? { autoHideDuration: 3000 } : { persist: true }),
    });

    await queryClient.invalidateQueries({
      queryKey: queryKeys.collections("media_objects")
    });
  };

  const initialValues = {
    amount: "",
    controlAmount: "",
    fileType: "",
    debitOrCredit: "debit",
    amountIsNetOrGrossAmount: "gross",
    vatOverwrite: "1.19",
    bookingText: "",
    suppressEmails: false,
    issuer: "LBRA",
    bookingDate: null,
  };
  const { values, handleChange, handleSubmit, handleBlur } = useForm({
    initialValues,
    onSubmit: async () => setConfirmCreateInvoiceOpen(true),
  });

  const phpRound = (value: number, precision = 2) => {
    const factor = 10 ** precision;

    let x = value * factor;

    x = x + (value >= 0 ? 0.5 : -0.5);

    return Math.trunc(x) / factor;
  };

  useEffect(() => {
    const fmt = new Intl.NumberFormat("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    });

    const controlAmount = values.amount
      ? formValue(values, "amountIsNetOrGrossAmount") !== "gross"
        ? fmt.format(phpRound(convertToFloat(values.amount) * parseFloat(values.vatOverwrite)))
        : fmt.format(phpRound(convertToFloat(values.amount) / parseFloat(values.vatOverwrite)))
      : 0;

    if (formValue(values, "controlAmount") !== controlAmount) {
      handleChange({ target: { name: "controlAmount", value: controlAmount } });
    }
  }, [values]);

  if (product.preventAccounting === true) {
    return (
      <Typography variant="h3" sx={{ textAlign: "center" }}>
        Konto/Rechnungen sind pausiert!
      </Typography>
    );
  }

  const fileType: string = formValue(values, "fileType");

  const confirmationConfig = invoiceConfirmationConfig[fileType] ?? invoiceConfirmationConfig.default;

  return (
    <ContentBox headline="Rechnung erstellen">
      <Typography sx={{ textAlign: "center", marginY: 2 }}>
        Achtung: Es werden hier nicht nur Rechnungsdateien generiert, sondern auch Buchungseinträge im Konto verursacht
        und teilweise auch die Rechnung direkt per E-Mail an die Rechtsschutzversicherung geschickt.
      </Typography>
      <ValidatorForm onSubmit={handleSubmit} submitOnEnter={false}>
        <Grid container spacing={3} sx={{
          alignItems: "center"
        }}>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <ValidatorSelect
              label={"Welche Rechnung?"}
              name={"fileType"}
              value={formValue(values, "fileType")}
              helperText={"Art der Rechnung"}
              onChange={handleChange}
            >
              {generatableInvoices[product.productClassName].map((fileData: GeneratableFile) =>
                fileData.isHidden && fileData.isHidden(product) ? null : (
                  <MenuItem
                    key={fileData.value}
                    value={fileData.value}
                    disabled={fileData.isDisabled?.(product) ?? false}
                  >
                    {fileData.label}
                  </MenuItem>
                )
              )}
            </ValidatorSelect>
          </Grid>
          {values.fileType === "genericInvoiceCreditNote" && (
            <>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <ValidatorSelect
                  label={"Gesellschaft"}
                  name={"issuer"}
                  value={formValue(values, "issuer")}
                  helperText={"Im Namen welcher Gesellschaft soll die Rechnung erstellt werden?"}
                  onChange={handleChange}
                >
                  <MenuItem value={"LBRA"}>Legalbird Rechtsanwälte Rechtsanwalts GmbH</MenuItem>
                  <MenuItem value={"RS"}>R&S Internet Jewels GmbH</MenuItem>
                </ValidatorSelect>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <ValidatorSelect
                  label={"Gutschrift oder Rechnung?"}
                  name={"debitOrCredit"}
                  value={formValue(values, "debitOrCredit")}
                  onChange={handleChange}
                >
                  <MenuItem value={"debit"}>Rechnung</MenuItem>
                  <MenuItem value={"credit"}>Gutschrift</MenuItem>
                </ValidatorSelect>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <ValidatorSelect
                  label={"Eingabeformat"}
                  name={"amountIsNetOrGrossAmount"}
                  value={formValue(values, "amountIsNetOrGrossAmount")}
                  onChange={handleChange}
                >
                  <MenuItem value={"gross"}>Brutto</MenuItem>
                  <MenuItem value={"net"}>Netto</MenuItem>
                </ValidatorSelect>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <ValidatorSelect
                  label={"MwSt.-Satz"}
                  name={"vatOverwrite"}
                  value={formValue(values, "vatOverwrite")}
                  onChange={handleChange}
                  helperText={"Der Mehrwertsteuersatz für die Berechnung."}
                >
                  <MenuItem value={"1.16"}>16%</MenuItem>
                  <MenuItem value={"1.19"}>19%</MenuItem>
                </ValidatorSelect>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <ValidatorNumberField
                  label={
                    formValue(values, "amountIsNetOrGrossAmount") === "gross" ? "Betrag (brutto)" : "Betrag (netto)"
                  }
                  name={"amount"}
                  value={formValue(values, "amount")}
                  numberType={"float"}
                  fieldType={"money"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={`Bitte trage hier den ${
                    formValue(values, "amountIsNetOrGrossAmount") === "gross" ? "Bruttobetrag" : "Nettobetrag"
                  } ein.`}
                  floatScaleThree={true}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <ValidatorTextField
                  label={"Buchungstext Mandantenkonten"}
                  name={"bookingText"}
                  value={formValue(values, "bookingText")}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={"Soll ein Buchungstext im Mandantenkonten zu sehen sein?"}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <ValidatorTextField
                  label={
                    formValue(values, "amountIsNetOrGrossAmount") === "gross" ? "Betrag (netto)" : "Betrag (brutto)"
                  }
                  name={"controlAmount"}
                  value={formValue(values, "controlAmount")}
                  onChange={handleChange}
                  InputProps={{
                    readOnly: true,
                  }}
                  helperText={`Der oben eingegebene ${
                    formValue(values, "amountIsNetOrGrossAmount") === "gross"
                      ? "Bruttobetrag abzüglich"
                      : "Nettobetrag zuzüglich"
                  } ${formValue(values, "vatOverwrite") === "1.19" ? "19%" : "16%"} MwSt.`}
                />
              </Grid>
              {formValue(values, "debitOrCredit") === "credit" &&
                formValue(values, "fileType") === "genericInvoiceCreditNote" && (
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <RelatedInvoiceNumberSelect
                      bookingEntries={bookingEntries}
                      value={formValue(values, "relatedInvoiceNumber")}
                      onChange={handleChange}
                    />
                  </Grid>
                )}
            </>
          )}
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <ButtonLoading type={"submit"} color={"primary"} variant={"contained"} fullWidth={true}>
              Rechnung erstellen
            </ButtonLoading>
          </Grid>
        </Grid>
      </ValidatorForm>
      <LegalbirdIoConfirm
        handleClose={() => setConfirmCreateInvoiceOpen(false)}
        headline={confirmationConfig.headline}
        open={confirmCreateInvoiceOpen}
        handleConfirm={generateInvoice}
        confirmText={confirmationConfig.buttonLabel}
        content={confirmationConfig.text}
      />
    </ContentBox>
  );
};

export default CaseInvoiceGenerateForm;
